import React, { useState, useEffect } from 'react';
import { Loader2, Check } from 'lucide-react';
import { authApi } from '../api/services';
import { useAuth } from '../auth/AuthProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// MfaSetupPage — three-step authenticator setup flow:
//   1. Scan QR code (or copy the shared key manually)
//   2. Verify the first 6-digit code
//   3. Save the recovery codes
//
// Lives inside the authenticated app shell, so it doesn't use the dark glass
// AuthBackdrop the unauthenticated auth pages share.
// ---------------------------------------------------------------------------

// Simple QR code generator using a public API (no dependency needed)
function QrCode({ value, size = 220 }: { value: string; size?: number }) {
  // High-contrast black-on-white renders cleanly in light and dark themes.
  const url = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(value)}&bgcolor=FFFFFF&color=000000&format=svg`;
  return (
    <div className="my-6 flex justify-center">
      <img
        src={url}
        alt="Scan this QR code with your authenticator app"
        width={size}
        height={size}
        className="rounded-lg border-2 border-border"
      />
    </div>
  );
}

const STEP_LABELS = ['Scan QR Code', 'Verify Code', 'Save Recovery Codes'];

export default function MfaSetupPage() {
  const { markMfaEnabled } = useAuth();
  const [step, setStep] = useState(0); // 0=loading, 1=show QR, 2=verify, 3=done
  const [qrUri, setQrUri] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setupMfa();
  }, []);

  const setupMfa = async () => {
    try {
      const data = await authApi.setupMfa();
      setQrUri(data.authenticatorUri || (data as any).qrUri || '');
      setManualKey(data.sharedKey || (data as any).key || (data as any).authenticatorKey || '');
      setStep(1);
    } catch (err: any) {
      setError('Failed to initialise MFA setup. Please try again.');
    }
  };

  const verifyAndEnable = async () => {
    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const result = await authApi.enableMfa(code);
      markMfaEnabled();
      setRecoveryCodes(result.recoveryCodes || []);
      setStep(3);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const activeStep = step === 0 ? 0 : step - 1;

  return (
    <div className="mx-auto max-w-2xl p-4 md:p-6">
      <h1 className="mb-1 text-2xl font-semibold tracking-tight">
        Set Up Multi-Factor Authentication
      </h1>
      <p className="mb-6 text-sm text-muted-foreground">
        Protect your account with an authenticator app like Microsoft Authenticator, Google Authenticator, or Authy.
      </p>

      <Card>
        <CardContent className="py-6">
          {/* Stepper */}
          <ol className="mb-6 flex items-center justify-between">
            {STEP_LABELS.map((label, i) => {
              const complete = i < activeStep;
              const current = i === activeStep;
              return (
                <React.Fragment key={label}>
                  <li className="flex flex-col items-center gap-2">
                    <div
                      className={cn(
                        'grid h-7 w-7 place-items-center rounded-full border-2 text-xs font-semibold',
                        complete && 'border-emerald-500 bg-emerald-500 text-white',
                        current && 'border-primary bg-primary text-primary-foreground',
                        !complete && !current && 'border-border text-muted-foreground',
                      )}
                    >
                      {complete ? <Check className="h-3.5 w-3.5" /> : i + 1}
                    </div>
                    <p
                      className={cn(
                        'text-[11px] font-medium',
                        current ? 'text-foreground' : 'text-muted-foreground',
                      )}
                    >
                      {label}
                    </p>
                  </li>
                  {i < STEP_LABELS.length - 1 && (
                    <div
                      aria-hidden
                      className={cn(
                        'mx-2 h-px flex-1',
                        i < activeStep ? 'bg-emerald-500' : 'bg-border',
                      )}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </ol>

          {error && (
            <Alert variant="error" className="mb-3">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Step 0: Loading */}
          {step === 0 && (
            <div className="py-10 text-center">
              <Loader2 className="mx-auto h-7 w-7 animate-spin text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">Preparing MFA setup...</p>
            </div>
          )}

          {/* Step 1: Show QR Code */}
          {step === 1 && (
            <>
              <p className="text-sm">Scan this QR code with your authenticator app:</p>
              {qrUri && <QrCode value={qrUri} size={220} />}
              <p className="text-sm text-muted-foreground">Can't scan? Enter this key manually:</p>
              <div className="mb-4 mt-2 rounded-lg border border-border bg-muted/40 p-3 text-center font-mono text-sm tracking-widest text-primary">
                {manualKey}
              </div>
              <Button onClick={() => setStep(2)} size="lg" className="w-full">
                I've scanned the code — Next
              </Button>
            </>
          )}

          {/* Step 2: Verify */}
          {step === 2 && (
            <>
              <p className="mb-4 text-sm">
                Enter the 6-digit code from your authenticator app to verify setup:
              </p>
              <div className="mb-4 flex flex-col gap-1.5">
                <Label htmlFor="mfa-code">6-digit code</Label>
                <Input
                  id="mfa-code"
                  inputMode="numeric"
                  maxLength={6}
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  onKeyDown={(e) => { if (e.key === 'Enter') verifyAndEnable(); }}
                  className="h-12 text-center font-mono text-2xl font-semibold tracking-[0.3em]"
                />
              </div>
              <Button
                onClick={verifyAndEnable}
                disabled={loading || code.length !== 6}
                size="lg"
                className="w-full gap-2"
              >
                {loading && <Loader2 className="h-5 w-5 animate-spin" />}
                {loading ? 'Verifying…' : 'Verify & Enable MFA'}
              </Button>
              <Button
                variant="ghost"
                onClick={() => setStep(1)}
                className="mt-1 w-full"
              >
                Back to QR code
              </Button>
            </>
          )}

          {/* Step 3: Recovery Codes */}
          {step === 3 && (
            <>
              <Alert variant="success" className="mb-4">
                <AlertDescription>MFA is now enabled on your account.</AlertDescription>
              </Alert>
              <p className="mb-1 text-base font-semibold">Save your recovery codes</p>
              <p className="mb-3 text-sm text-muted-foreground">
                If you lose access to your authenticator app, you can use these one-time codes to
                sign in. Store them somewhere safe — you won't be able to see them again.
              </p>
              <div className="mb-4 rounded-lg border border-border bg-muted/40 p-3">
                {recoveryCodes.map((rc, i) => (
                  <p key={i} className="py-0.5 font-mono text-sm text-emerald-600 dark:text-emerald-400">
                    {rc}
                  </p>
                ))}
              </div>
              <Button
                onClick={() => navigator.clipboard.writeText(recoveryCodes.join('\n'))}
                className="mb-2 w-full"
              >
                Copy Recovery Codes
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  // Clear session to force re-auth with updated MFA status
                  sessionStorage.clear();
                  window.location.href = '/';
                }}
                className="w-full"
              >
                Done — Sign in again
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
